import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Players from '@/pages/Players';
import PlayerDetail from '@/pages/PlayerDetail';
import Users from '@/pages/Users';
import Investments from '@/pages/Investments';
import Analytics from '@/pages/Analytics';
import Finance from '@/pages/Finance';
import ActivityHistory from '@/pages/ActivityHistory';
import About from '@/pages/About';
import Manual from '@/pages/Manual';
import Signings from '@/pages/Signings';
import PlayerForm from '@/pages/PlayerForm';
import ScoutCodes from '@/pages/ScoutCodes';
import FormulariosRecibidos from '@/pages/FormulariosRecibidos';
import Seguimiento from '@/pages/Seguimiento';
import SeguimientoForm from '@/pages/SeguimientoForm';
import SeguimientoDetail from '@/pages/SeguimientoDetail';
import Prospects from '@/pages/Prospects';
import Interactions from '@/pages/Interactions';
import Categories from '@/pages/Categories';
import Payments from '@/pages/Payments';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ConfigProvider } from '@/context/ConfigContext';
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute, { getDefaultRouteForRole } from '@/components/ProtectedRoute';

/**
 * Roles del sistema (4):
 * - superadmin: acceso total + gestión de usuarios
 * - admin: todo excepto usuarios
 * - legales: contratos + fichas de jugadores
 * - scout: seguimientos, jugadores, solicitar materiales
 */

function AppContent() {
  const { user, role } = useAuth();

  return (
    <>
      <Routes>
        {/* Public pages */}
        <Route path="/formulario" element={<PlayerForm />} />

        {/* Protected docs */}
        <Route
          path="/about"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'legales', 'scout']}>
              <About />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manual"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'legales', 'scout']}>
              <Manual />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={user ? <Navigate to={getDefaultRouteForRole(role)} replace /> : <Login />}
        />

        {/* Dashboard */}
        <Route
          path="/"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'legales']}>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Players */}
        <Route
          path="/players"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'legales', 'scout']}>
              <Players />
            </ProtectedRoute>
          }
        />
        <Route
          path="/players/:id"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'legales', 'scout']} redirectTo="/players">
              <PlayerDetail />
            </ProtectedRoute>
          }
        />

        {/* Seguimiento (scouting) */}
        <Route
          path="/seguimiento"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']}>
              <Seguimiento />
            </ProtectedRoute>
          }
        />
        <Route
          path="/seguimiento/nuevo"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']}>
              <SeguimientoForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/seguimiento/:id"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']} redirectTo="/seguimiento">
              <SeguimientoDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/seguimiento/:id/editar"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']} redirectTo="/seguimiento">
              <SeguimientoForm />
            </ProtectedRoute>
          }
        />

        {/* Config pages */}
        <Route
          path="/signings"
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <Signings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/investments"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']}>
              <Investments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
              <Finance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
              <ActivityHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pagos-mensuales"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
              <Payments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scout-codes"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
              <ScoutCodes />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jugadores-de-interes"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']}>
              <Prospects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reuniones-y-notas"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']}>
              <Interactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/formularios-recibidos"
          element={
            <ProtectedRoute allowedRoles={['superadmin', 'admin', 'scout']}>
              <FormulariosRecibidos />
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <ConfigProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
